import React, { useState, useEffect } from 'react';
import {
    Paper,
    Text,
    Group,
    Stack,
    Avatar,
    Divider,
    Button,
    NumberInput,
    Accordion,
    ActionIcon,
    Box,
    createStyles,
    Grid,
    Badge,
    Title,
    rem,
} from '@mantine/core';
import { IconPencil, IconX, IconInfoCircle, IconPlus, IconTrendingUp } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F8F9FA', // Light grey background like in the image
        borderLeft: `1px solid ${theme.colors.gray[3]}`,
    },
    header: {
        backgroundColor: 'white',
        padding: theme.spacing.md,
        borderRadius: theme.radius.md,
        marginBottom: theme.spacing.md,
    },
    sectionCard: {
        backgroundColor: 'white', // White card background
        padding: theme.spacing.md,
        borderRadius: theme.radius.md,
        marginBottom: theme.spacing.md,
        boxShadow: theme.shadows.xs,
    },
    lopCard: {
        backgroundColor: 'white',
        padding: theme.spacing.sm,
        borderRadius: theme.radius.md,
        textAlign: 'center',
        position: 'relative',
        boxShadow: theme.shadows.xs,
        border: `1px solid ${theme.colors.gray[2]}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    actualPayableCard: {
        backgroundColor: '#FA5252', // Red color
        color: 'white',
        padding: theme.spacing.sm,
        borderRadius: theme.radius.md,
        textAlign: 'center',
        boxShadow: theme.shadows.sm,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    earningsHeader: {
        borderLeft: '4px solid #40C057', // Green border
        paddingLeft: theme.spacing.xs,
        marginBottom: theme.spacing.md,
    },
    deductionsHeader: {
        borderLeft: '4px solid #FA5252', // Red border
        paddingLeft: theme.spacing.xs,
        marginBottom: theme.spacing.md,
    },
    itemRow: {
        padding: theme.spacing.sm,
        backgroundColor: '#F1F3F5',
        borderRadius: theme.radius.sm,
        marginBottom: theme.spacing.xs,
    },
    footer: {
        backgroundColor: 'white',
        padding: theme.spacing.md,
        borderTop: `1px solid ${theme.colors.gray[2]}`,
        marginTop: 'auto', // Push to bottom
    }
}));

// Define the shape of the employee data prop (mirroring what's available or needed)
interface EmployeeData {
    id: string;
    name: string;
    designation: string;
    image?: string;
    netPay: number;
    payableDays: number;
    lopDays: number;
    earnings: { label: string; amount: number; subtext?: string }[];
    deductions: { label: string; amount: number }[];
}

interface PayrollEmployeeSidePanelProps {
    employee: any; // Using any for now to easily map from parent, can refine strict type later
    onClose: () => void;
    onSave: (data: any) => void;
}

export default function PayrollEmployeeSidePanel({ employee, onClose, onSave }: PayrollEmployeeSidePanelProps) {
    const { classes } = useStyles();
    const [lopDays, setLopDays] = useState<number>(0);
    const [adjustmentDays, setAdjustmentDays] = useState<number>(0);

    // Initialize state when employee changes
    useEffect(() => {
        if (employee) {
            setLopDays(employee.lop_days || 0);
            setAdjustmentDays(employee.adjustment_days || 0);
        }
    }, [employee]);

    // Mock data if employee doesn't have full details yet
    const formatCurrency = (val: number) => `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    const earnings = [
        { label: 'Basic Salary', amount: employee?.calculated_salary?.basic || 0 },
        { label: 'House Rent Allowance', amount: employee?.calculated_salary?.house_rent_allowance || 0 },
        { label: 'Conveyance Allowance', amount: employee?.calculated_salary?.conveyance_allowance || 0 },
        { label: 'Medical Allowance', amount: employee?.calculated_salary?.medical_allowance || 0 },
        { label: 'Helper Allowance', amount: employee?.calculated_salary?.helper_allowance || 0 },
    ].filter(item => item.amount > 0);

    const deductions = [
        { label: 'Loan Deduction', amount: employee?.loan_deduction || 0 },
    ].filter(item => item.amount > 0);

    if (deductions.length === 0) {
        deductions.push({ label: 'No Deductions', amount: 0 });
    }

    const totalDaysInMonth = employee?.days_in_month || 30;

    // Reconstruct the system-calculated payable days (before manual LOP adjustments)
    const systemPayableDays = (employee?.present_days || 0) +
        (employee?.holiday_count || 0) +
        (employee?.non_working_days || 0) +
        (employee?.paid_leave_count || 0);

    // Final result after manual LOP and Adjustments (like forgotten check-ins)
    const actualPayableDays = Math.max(0, systemPayableDays - lopDays + adjustmentDays);

    // Map employee data coming from Firestore to our UI needs
    const displayName = employee?.name || `${employee?.first_name || ''} ${employee?.last_name || ''}`;
    const designation = employee?.designation || 'Employee';

    // Base salary (CTC) - get the FIXED full-month value to avoid recursive shrinking
    const fixedMonthlyCtc = employee?.fixed_monthly_ctc
        || employee?.fixed_monthly_breakdown?.monthly_ctc
        || 0;

    // Helper to scale any component based on actual payable days
    const scaleComponent = (fixedAmount: number) =>
        totalDaysInMonth > 0 ? (fixedAmount / totalDaysInMonth) * actualPayableDays : 0;

    // Net pay calculation for the header and total
    const calculatedNetPay = scaleComponent(fixedMonthlyCtc);

    const handleSave = () => {
        // Scale all components of the breakdown so they match the new total
        const updatedBreakdown = {
            basic: scaleComponent(employee?.fixed_monthly_breakdown?.basic || 0),
            house_rent_allowance: scaleComponent(employee?.fixed_monthly_breakdown?.house_rent_allowance || 0),
            conveyance_allowance: scaleComponent(employee?.fixed_monthly_breakdown?.conveyance_allowance || 0),
            medical_allowance: scaleComponent(employee?.fixed_monthly_breakdown?.medical_allowance || 0),
            helper_allowance: scaleComponent(employee?.fixed_monthly_breakdown?.helper_allowance || 0),
            monthly_ctc: Math.round(calculatedNetPay)
        };

        onSave({
            ...employee,
            lop_days: lopDays,
            adjustment_days: adjustmentDays,
            total_payable_days: actualPayableDays,
            calculated_salary: updatedBreakdown
        });
    };

    return (
        <Box className={classes.root} p="md">
            {/* Header */}
            <Paper className={classes.header}>
                <Group position="apart">
                    <Group>
                        <Avatar src={employee?.image} size={50} radius="xl" color="red">
                            {displayName.charAt(0)}
                        </Avatar>
                        <div>
                            <Text weight={700} color="red.7" size="lg">{displayName}</Text>
                            <Text size="xs" color="dimmed">{designation}</Text>
                        </div>
                    </Group>
                    <div style={{ textAlign: 'right' }}>
                        <Text size="xs" color="dimmed">Net Pay</Text>
                        <Text weight={700} color="blue" size="lg">{formatCurrency(calculatedNetPay)}</Text>
                    </div>
                </Group>
            </Paper>

            <Grid gutter="xs" mb="md">
                <Grid.Col span={3}>
                    <Paper className={classes.lopCard}>
                        <Text size="xs" color="dimmed" mb={4}>System Days</Text>
                        <Text weight={700} size="xl" color="blue.6">{systemPayableDays}</Text>
                    </Paper>
                </Grid.Col>

                <Grid.Col span={3}>
                    <Paper className={classes.lopCard}>
                        <Text size="xs" color="dimmed" mb={4}>LOP Days</Text>
                        <NumberInput
                            value={lopDays}
                            onChange={(val) => setLopDays(val || 0)}
                            min={0}
                            max={systemPayableDays}
                            size="xs"
                            variant="unstyled"
                            styles={{
                                input: {
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    fontSize: rem(20),
                                    color: '#FA5252',
                                    minHeight: 'unset',
                                    height: 'auto',
                                    padding: 0
                                },
                                control: {
                                    border: 'none',
                                }
                            }}
                            hideControls={false}
                        />
                    </Paper>
                </Grid.Col>

                <Grid.Col span={3}>
                    <Paper className={classes.lopCard}>
                        <Text size="xs" color="dimmed" mb={4}>Add Days</Text>
                        <NumberInput
                            value={adjustmentDays}
                            onChange={(val) => setAdjustmentDays(val || 0)}
                            min={0}
                            max={totalDaysInMonth - systemPayableDays}
                            size="xs"
                            variant="unstyled"
                            styles={{
                                input: {
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    fontSize: rem(20),
                                    color: '#40C057',
                                    minHeight: 'unset',
                                    height: 'auto',
                                    padding: 0
                                },
                                control: {
                                    border: 'none',
                                }
                            }}
                            hideControls={false}
                        />
                    </Paper>
                </Grid.Col>

                <Grid.Col span={3}>
                    <Paper className={classes.actualPayableCard}>
                        <Text size="xs" color="white" style={{ opacity: 0.9 }} mb={4}>Payable Days</Text>
                        <Text weight={700} size="xl" color="white">{actualPayableDays}</Text>
                    </Paper>
                </Grid.Col>
            </Grid>

            {/* LOP Adjustments Accordion */}
            <Paper className={classes.sectionCard} p={0}>
                <Accordion variant="separated" radius="md">
                    <Accordion.Item value="adjustments" sx={{ border: 'none' }}>
                        <Accordion.Control>
                            <Group>
                                <IconInfoCircle size={16} />
                                <Text size="sm">Past Month LOP Adjustments</Text>
                            </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Text size="xs" color="dimmed">No past adjustments.</Text>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
                <Text size="xs" color="red" p="xs" style={{ cursor: 'pointer' }}>+ Add More</Text>
            </Paper>

            {/* Earnings Section */}
            <Paper className={classes.sectionCard}>
                <Group position="apart" className={classes.earningsHeader}>
                    <Text weight={600}>Earnings Breakdown</Text>
                    <Text size="xs" color="green" style={{ cursor: 'pointer' }}>+ Add Earning</Text>
                </Group>

                <Stack spacing="xs">
                    {earnings.map((item, idx) => (
                        <Group key={idx} position="apart" className={classes.itemRow}>
                            <Text size="sm">{item.label}</Text>
                            <Text size="sm" weight={500}>{formatCurrency(item.amount)}</Text>
                        </Group>
                    ))}
                </Stack>
            </Paper>

            {/* Deductions Section */}
            <Paper className={classes.sectionCard}>
                <Group position="apart" className={classes.deductionsHeader}>
                    <Text weight={600}>Tax & Deductions</Text>
                    <Text size="xs" color="red" style={{ cursor: 'pointer' }}>+ Add Deduction</Text>
                </Group>

                <Stack spacing="xs">
                    {deductions.map((item, idx) => (
                        <Group key={idx} position="apart" className={classes.itemRow}>
                            <Text size="sm">{item.label}</Text>
                            <Group spacing={4}>
                                <Text size="sm" weight={500}>{formatCurrency(item.amount)}</Text>
                                <ActionIcon size="xs" color="red"><IconPencil size={12} /></ActionIcon>
                            </Group>
                        </Group>
                    ))}
                </Stack>
            </Paper>

            {/* Footer */}
            <Paper className={classes.footer}>
                <Group position="apart" mb="md">
                    <Text size="xs" transform="uppercase" color="dimmed">Net Pay</Text>
                </Group>
                <Group position="apart">
                    <Text size="xl" weight={700} color="red">{formatCurrency(calculatedNetPay)}</Text>
                    <Group>
                        <Button variant="default" onClick={onClose}>Cancel</Button>
                        <Button color="red" onClick={handleSave}>Save</Button>
                    </Group>
                </Group>
            </Paper>

        </Box>
    );
}
